import { Link } from "react-router-dom";
import LegalShell, { Clause } from "@/components/LegalShell";
import { useSiteConfig } from "@/lib/siteConfig";

/**
 * The Privacy Policy.
 *
 * **Why it has to exist as its own page.** Clause 9 of the terms promises "the Platform's Privacy
 * Policy", and both sign-in providers ask for its URL: Google's consent screen shows it to every
 * customer, and Meta will not switch a Facebook app to Live without one (deploy/OAUTH.md §2). So
 * this page is a go-live dependency, not a formality — see BACKLOG.md §1.5.
 *
 * **Everything here describes what the code actually does**, which is the only way a policy stays
 * true: the categories in clause 2 are the fields the wizard and the profile collect, clause 5 is
 * who the role checks let through, clause 6 is `core/crypto.py` and the audit trail, and the
 * Telegram line is `core/telegram.py`'s deliberate omission of customer identity. If a change makes
 * one of these sentences false, the sentence moves — not the other way round.
 *
 * `PRIVACY_VERSION` is **not** `TERMS_VERSION`. The terms version is recorded against every order
 * as the revision the customer accepted at payment, so the UI suite guards the pairing of prose and
 * date (frontend/tests/e2e/routes.mjs). A privacy policy is a notice rather than a bargain: nothing
 * records which revision was shown, so the date lives here beside the text it describes.
 */
export const PRIVACY_VERSION = "4 October 2026";

export default function Privacy() {
  const cfg = useSiteConfig();
  const company = (cfg.company_name || "DharmaTN").trim();
  const address = (cfg.address || "").trim();
  return (
    <LegalShell title="Privacy Policy" updated={PRIVACY_VERSION} backTestId="privacy-back">
      <div className="space-y-8" data-testid="privacy-body">

        <p>This policy explains what personal data DharmaTN collects, why, who can see it, how long
        it is kept, and what you can ask us to do with it. It applies to the website at
        www.dharmatn.in and to every service ordered through it.</p>

        <p><strong>In short:</strong> we collect what a document needs and what delivery and
        invoicing require, we show it only to the people working on your case, we never sell it or
        use it for advertising, and there is no advertising network, third-party analytics or session
        recording anywhere on this site.</p>

        {/* The legal name and the registered address are **not written into this prose**. They
            come from Company details in Admin → Settings — the same two keys the invoice's FROM
            block is rendered from (`COMPANY_FIELDS` in core/templates_data.py), so who this
            business is cannot say one thing on the bill and another on the page that identifies
            the data fiduciary. The address block is omitted rather than left as an empty heading
            when the setting is blank; it should not be blank, because the Consumer Protection
            (E-Commerce) Rules 2020 require the principal geographic address to be displayed. */}
        <Clause title="1. Who is responsible for your data">
          <p>{company} operates the Platform and is the data fiduciary for the personal data
          described below, as that term is used in the Digital Personal Data Protection Act, 2023.
          Questions, requests and grievances go to{" "}
          <a href={`mailto:${cfg.support_email}`} data-testid="privacy-email"
             className="underline text-[#CBA96C] hover:opacity-80 break-all">{cfg.support_email}</a>.</p>
          {address && (
            <div className="space-y-1">
              <div className="font-bold text-[#DDE3EE] text-sm">Registered address</div>
              <p className="whitespace-pre-line" data-testid="privacy-address">{address}</p>
            </div>
          )}
        </Clause>

        <Clause title="2. What we collect">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Your sign-in identity.</strong> You sign in with Google or Facebook, and we
            receive your name, email address, profile picture and that provider&rsquo;s account
            identifier. We never receive your Google or Facebook password, and customer accounts on
            the Platform hold no password of their own.</li>
            <li><strong>Contact and delivery details.</strong> Your phone number and postal address,
            used to deliver a printed document and to print the address on your invoice.</li>
            <li><strong>The contents of your document.</strong> Everything entered into a template or
            supplied as a draft: names, addresses, dates, amounts, and identity numbers such as
            Aadhaar or PAN where the document itself calls for them. This may include details of
            other people named in your document, which you provide on the basis set out in clause 8
            below.</li>
            <li><strong>Files you upload.</strong> Identity and supporting documents attached to a
            case.</li>
            <li><strong>Payment records.</strong> Invoice number, amount, the payment gateway&rsquo;s
            own reference and, once the money settles, the bank reference. <strong>Card, UPI and net
            banking credentials are entered on the payment gateway&rsquo;s page and never reach the
            Platform.</strong></li>
            <li><strong>Case correspondence.</strong> Messages exchanged about a case between you,
            our staff and the advocate assigned to it.</li>
            <li><strong>Technical records.</strong> IP address, the requests your browser makes,
            timestamps and sign-in attempts, kept to operate the service, debug failures and detect
            abuse.</li>
          </ul>
          <p>We do not collect special categories of data beyond the identity details a document
          requires, and we ask for nothing that a requested service does not need.</p>
        </Clause>

        <Clause title="3. Why we use it">
          <p>To prepare the document you asked for; to arrange its printing on stamp paper; to have
          it reviewed by an advocate where the service you chose expressly includes that; to deliver
          it; to raise an invoice and keep the accounting and tax records the law requires us to
          keep; to answer your questions and support your case; to keep the Platform secure and
          prevent fraud or misuse; and to comply with legal obligations.</p>
          <p>We do not use your data to advertise to you, we do not profile you, and we do not take
          automated decisions about you.</p>
        </Clause>

        <Clause title="4. The basis on which we process it, and withdrawing consent">
          <p>We process your data with your consent, given when you sign in and when you place an
          order, and otherwise as necessary to perform the service you have ordered or to meet a
          legal obligation.</p>
          <p>You may withdraw your consent at any time by writing to the address in clause 1.
          Withdrawal stops further processing for the purposes you withdraw from, which generally
          means we can no longer provide the service, and it does not affect processing already
          carried out. Records we are required by law to retain — invoices and accounting records in
          particular — are kept for the period the law prescribes, as described in clause 7.</p>
        </Clause>

        <Clause title="5. Who can see it">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Our own staff.</strong> Administrators who operate the Platform, and the
            advocate assigned to your case — who sees that case, not our other customers&rsquo;
            cases. Every action taken on a case is recorded in an internal history showing who did
            what and when.</li>
            <li><strong>Service providers who carry out part of the work.</strong> The licensed stamp
            paper vendor and printer, the courier or postal service that delivers your document, the
            payment gateway that collects your payment, and the cloud hosting and database provider
            that runs the Platform. They receive only what their part of the work requires.</li>
            <li><strong>Operational alerts.</strong> Our team receives an automated message when a
            payment completes. It deliberately carries <strong>no customer identity</strong> — only
            the invoice number, the document type, the amount and an internal link.</li>
            <li><strong>Where the law compels it.</strong> A court order, or a lawful demand from an
            authority entitled to make it.</li>
          </ul>
          <p><strong>We do not sell, rent or trade personal data, and we share none of it with
          advertisers or data brokers.</strong></p>
        </Clause>

        <Clause title="6. Where it is stored, and how it is protected">
          <p>The Platform runs on cloud infrastructure operated by our hosting provider, and your
          data, including backups, may be stored and processed on servers located outside India.</p>
          <p>Traffic between your browser and the Platform is encrypted in transit (HTTPS). Uploaded
          documents and generated PDFs are encrypted at rest with AES-256-GCM, with the key held
          outside the database, so a copy of the database or of a backup volume alone does not
          disclose them. Access is limited by role: a customer reaches only their own cases, an
          advocate only the cases assigned to them. Sign-in is through Google or Facebook, so no
          customer password exists on the Platform to be stolen. Staff access is separately
          password-protected, and the database is backed up on a schedule with restores restricted to
          a path that cannot be triggered from a browser session alone.</p>
          <p>No system is perfectly secure. If a breach affects your personal data we will notify
          you and the Data Protection Board of India as the law requires.</p>
        </Clause>

        <Clause title="7. How long we keep it">
          <p>Your account, your cases and the documents attached to them are kept while your account
          exists, because a stamped document and the record of how it was produced are what you may
          later need to rely on. We erase them when they are no longer needed for the purpose you
          gave them for, or when you ask us to — except where the law requires us to keep a record.</p>
          <p><strong>Where the period is fixed by law:</strong> invoices and payment records are kept
          for <strong>eight years</strong> from the end of the financial year, which covers what the
          GST and income-tax rules require of us and runs longer while any assessment, appeal or
          investigation is open; request and sign-in logs are kept for <strong>at least twelve
          months</strong>, the minimum the Digital Personal Data Protection Rules, 2025 set for logs,
          and are discarded after that.</p>
          <p>You may ask us to erase your data at the address in clause 1. We will do so except where
          we are required to keep it, and we will tell you which records those are and why.</p>
        </Clause>

        <Clause title="8. Data about other people in your document">
          <p>A document often names someone other than you — the other party to an agreement, a
          witness, a family member. By submitting their details you confirm you are entitled to do so
          and that the details are accurate. We use them only to produce and deliver the document you
          ordered. A person named in your document may write to us at the address in clause 1 about
          the data we hold about them.</p>
        </Clause>

        <Clause title="9. Your rights">
          <p>Subject to the Digital Personal Data Protection Act, 2023 and the rules under it, you
          may ask us to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>tell you what personal data of yours we hold and what we have done with it;</li>
            <li>correct or complete anything inaccurate — your name, email, phone number and address
            are also editable yourself, in your profile;</li>
            <li>erase data we no longer need, subject to clause 7;</li>
            <li>nominate someone to exercise these rights on your behalf if you die or become
            incapable of exercising them.</li>
          </ul>
          <p>Write to the address in clause 1 and we will respond within a reasonable period. If you
          are not satisfied with how we have handled a grievance, you may complain to the Data
          Protection Board of India.</p>
        </Clause>

        <Clause title="10. Cookies and browser storage">
          <p>We set a sign-in cookie that your browser sends back to identify your session, and a
          short-lived cookie during a sign-in redirect that exists to verify that the sign-in
          returning to us is the one that left. The Platform also keeps a copy of your session token
          and a few interface preferences in your browser&rsquo;s own storage.</p>
          <p>That is all. There are no advertising cookies, no third-party analytics, no tracking
          pixels and no session recording on this site, and no third-party script runs on the pages
          where you type your details.</p>
        </Clause>

        <Clause title="11. Signing in with Google or Facebook">
          <p>Sign-in is handled by the provider you choose, on their own page. They will know that
          you signed in to DharmaTN, and their own privacy policies govern what they do with that.
          We receive your name, email address, profile picture and account identifier, and nothing
          else; we do not read your contacts, your posts or your mail, and we never post anything on
          your behalf. An email address is required because it is where your documents and invoices
          are sent.</p>
        </Clause>

        <Clause title="12. Children">
          <p>The Platform is for persons aged 18 and over, who alone can enter into the agreements it
          produces. We do not knowingly collect personal data of children. If you believe a child has
          used the Platform, write to the address in clause 1 and we will remove the data.</p>
        </Clause>

        <Clause title="13. Changes to this policy">
          <p>We may update this policy. The revised version is published on this page with a new
          &ldquo;Last updated&rdquo; date, and continued use of the Platform after publication means
          you accept it. Where a change materially affects how we use data you have already given
          us, we will say so rather than rely on the date alone.</p>
        </Clause>

        <Clause title="14. How this policy relates to the terms">
          <p>This policy is the Privacy Policy referred to in clause 9 of the{" "}
          <Link to="/disclaimer" data-testid="privacy-terms-link"
                className="underline text-[#CBA96C] hover:opacity-80">Disclaimer &amp; Terms of Use</Link>.
          Where the two deal with the same subject, this policy governs the handling of personal data
          and the terms govern the service itself.</p>
        </Clause>

      </div>

      {/* The support address is admin-configurable, so it cannot be written into the prose. */}
      <p className="border-t border-white/5 pt-6">For any question about this policy, or to exercise
      any right under it, contact <a href={`mailto:${cfg.support_email}`} data-testid="privacy-contact"
      className="underline text-[#CBA96C] hover:opacity-80 break-all">{cfg.support_email}</a>.</p>
    </LegalShell>
  );
}
